import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import styled from 'styled-components';
import { register as registerApi } from '../../../services/api';

// Styled components
const FormContainer = styled.div`
  max-width: 400px;
  margin: 2rem auto;
  padding: 2rem;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
`;

const FormTitle = styled.h2`
  text-align: center;
  color: #333;
  margin-bottom: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #555;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #4a90e2;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 0.75rem;
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #357abd;
  }
  
  &:disabled {
    background-color: #a0c4f7;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.p`
  color: #e53e3e;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const FormFooter = styled.div`
  margin-top: 1.5rem;
  text-align: center;
  color: #4b5563;
  
  a {
    color: #4a90e2;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Spinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// Add some additional styles
const StyledForm = styled.form`
  .required {
    color: #e53e3e;
    margin-left: 2px;
  }
  
  .hint {
    font-size: 0.8rem;
    color: #4b5563;
    margin-top: 0.25rem;
  }
`;

// Password visibility toggle component
const PasswordInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const TogglePasswordButton = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #4b5563;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #333;
  }
  
  &:focus {
    outline: 2px solid #4d90fe;
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const RegisterForm = () => {
  // Form validation schema
  const validationSchema = yup.object().shape({
    email: yup
      .string()
      .required('Email is required')
      .email('Please enter a valid email address'),
    confirmEmail: yup
      .string()
      .oneOf([yup.ref('email'), null], 'Emails must match')
      .required('Please confirm your email address'),
    password: yup
      .string()
      .required('Password is required')
      .min(8, 'Password must be at least 8 characters')
      .matches(/[a-z]/, 'Must contain at least one lowercase character')
      .matches(/[A-Z]/, 'Must contain at least one uppercase character')
      .matches(/[0-9]/, 'Must contain at least one number')
      .matches(/[!@#$%^&*]/, 'Must contain at least one special character'),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password'), null], 'Passwords must match')
      .required('Please confirm your password'),
    firstName: yup
      .string()
      .required('First name is required')
      .matches(/^[a-zA-Z\s'-]+$/, 'Name can only contain letters, spaces, hyphens, and apostrophes'),
    lastName: yup
      .string()
      .required('Last name is required')
      .matches(/^[a-zA-Z\s'-]+$/, 'Name can only contain letters, spaces, hyphens, and apostrophes'),
    phone: yup
      .string()
      .matches(/^[\d\s-+()]*$/, 'Please enter a valid phone number')
      .nullable(),
    studioName: yup
      .string()
      .max(80, 'Artist / studio name is too long')
      .matches(/^[a-zA-Z0-9\s'&.,-]*$/, 'Use letters, numbers, spaces, and basic punctuation only')
      .nullable(),
  });

  const { 
    register: formRegister, 
    handleSubmit, 
    formState: { errors, isSubmitting },
    watch
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    reValidateMode: 'onChange'
  });
  
  // Watch form values for real-time validation
  const watchFields = watch();
  
  // Form state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  
  // Auth context
  const { error: authError, clearError } = useAuth();
  const navigate = useNavigate();

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    if (field === 'password') {
      setShowPassword(!showPassword);
    } else {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  // Clear auth errors when form changes
  useEffect(() => {
    if (authError) {
      clearError();
    }
  }, [watchFields, authError, clearError]);

  // Check if email exists
  const checkEmailExists = async (email) => {
    try {
      // You might want to create a specific API endpoint for this check
      const response = await fetch(`/api/v1/auth/check-email?email=${encodeURIComponent(email)}`);
      const data = await response.json();
      return data.exists;
    } catch (error) {
      console.error('Error checking email:', error);
      return false;
    }
  };

  // Handle email blur to check if it exists
  const handleEmailBlur = async (e) => {
    const email = e.target.value;
    if (email && email.includes('@')) {
      const exists = await checkEmailExists(email);
      setEmailExists(exists);
    } else {
      setEmailExists(false);
    }
  };

const onSubmit = async (data) => {
  console.log('Form submitted with data:', data);
  try {
    const formData = {
      email: data.email,
      password: data.password,
      role: 'artist',
      profile: {
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone || '',
        studioName: data.studioName || ''
      }
    };
    
    console.log('Sending request to /auth/register with:', formData);
    const response = await registerApi(formData);
    console.log('Registration successful:', response.data);
    // Save the token to localStorage
    localStorage.setItem('token', response.data.token);
    // Redirect to registration success page, including the email for confirmation messaging
    navigate('/registration-success', { state: { email: data.email } });
  } catch (error) {
    console.error('Registration error:', error);
    if (error.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
      alert(error.response.data.message || 'Registration failed');
    } else {
      alert('Could not connect to the server. Please try again later.');
    }
  }
};

  return (
    <FormContainer>
      <FormTitle>Create an Account</FormTitle>
      
      {authError && <ErrorMessage>{authError}</ErrorMessage>}
      <div className="hint">
        Please check your Junk or Spam email folder for the verification email after registering.
      </div>
      
      <StyledForm onSubmit={handleSubmit(onSubmit)} noValidate>
        <input type="hidden" {...formRegister('role')} value="artist" />

        <FormGroup>
          <Label htmlFor="email">
            Email Address <span className="required">*</span>
          </Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="Enter your email"
            $hasError={!!errors.email || emailExists}
            onBlur={handleEmailBlur}
            {...formRegister('email')}
          />
          {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
          {emailExists && (
            <ErrorMessage>
              An account with this email already exists. <Link to="/login">Log in here</Link> or use a different email.
            </ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="confirmEmail">
            Confirm Email <span className="required">*</span>
          </Label>
          <Input
            id="confirmEmail"
            type="email"
            autoComplete="email"
            placeholder="Re-enter your email"
            $hasError={!!errors.confirmEmail}
            {...formRegister('confirmEmail')}
          />
          {errors.confirmEmail && <ErrorMessage>{errors.confirmEmail.message}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="password">
            Password <span className="required">*</span>
          </Label>
          <PasswordInputWrapper>
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Create a password"
              autoComplete="new-password"
              $hasError={!!errors.password}
              {...formRegister('password')}
            />
            <TogglePasswordButton 
              type="button" 
              onClick={() => togglePasswordVisibility('password')}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? '👁️' : '👁️‍🗨️'}
            </TogglePasswordButton>
          </PasswordInputWrapper>
          {errors.password ? (
            <ErrorMessage>{errors.password.message}</ErrorMessage>
          ) : (
            <div className="hint">
              Must be at least 8 characters with uppercase, lowercase, number, and special character
            </div>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="confirmPassword">
            Confirm Password <span className="required">*</span>
          </Label>
          <PasswordInputWrapper>
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm your password"
              autoComplete="new-password"
              $hasError={!!errors.confirmPassword}
              {...formRegister('confirmPassword')}
            />
            <TogglePasswordButton 
              type="button" 
              onClick={() => togglePasswordVisibility('confirm')}
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
            </TogglePasswordButton>
          </PasswordInputWrapper>
          {errors.confirmPassword && (
            <ErrorMessage>{errors.confirmPassword.message}</ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="firstName">
            First Name <span className="required">*</span>
          </Label>
          <Input
            id="firstName"
            type="text"
            {...formRegister('firstName')}
            placeholder="First Name"
            $hasError={!!errors.firstName}
          />
          {errors.firstName && (
            <ErrorMessage>{errors.firstName.message}</ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="lastName">
            Last Name <span className="required">*</span>
          </Label>
          <Input
            id="lastName"
            type="text"
            {...formRegister('lastName')}
            placeholder="Last Name"
            $hasError={!!errors.lastName}
          />
          {errors.lastName && (
            <ErrorMessage>{errors.lastName.message}</ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="phone">Phone Number</Label>
          <Input
            id="phone"
            type="tel"
            {...formRegister('phone')}
            placeholder="Phone Number (Optional)"
            $hasError={!!errors.phone}
          />
          {errors.phone && (
            <ErrorMessage>{errors.phone.message}</ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="studioName">Artist / studio name</Label>
          <Input
            id="studioName"
            type="text"
            {...formRegister('studioName')}
            placeholder="How you want clients to see your name"
            $hasError={!!errors.studioName}
          />
          {errors.studioName && (
            <ErrorMessage>{errors.studioName.message}</ErrorMessage>
          )}
        </FormGroup>

        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? <Spinner /> : 'Create Account'}
        </Button>

        <FormFooter>
          Already have an account? <Link to="/login">Log in</Link>
        </FormFooter>
      </StyledForm>
    </FormContainer>
  );
};

export default RegisterForm;
